/**
 * @author        tae@zumper.com (Tae Yoon)
 * @copyright     Copyright (c) 2019, Zumper
 * @description   Detail entity selectors
 */

import { selectNow } from '@zcode-ui/web-zumper-global-state'
import { BuildingAmenity, ListingAmenity, PropertyFeatures } from '@zcode/enums'
import { createSelector } from '@zcode/memoizers'
import {
  BOOKINGDOTCOM,
  EVOLVE,
  JUNE_HOMES,
  RENTALS_UNITED,
  VRBO,
} from '@zcode/nr/es/shared/constants/source'
import {
  selectError,
  selectFetching,
  selectFetchingKey,
} from '@zcode/nr/es/shared/modules/meta/selectors'
import { oisEmpty } from '@zcode/nr/es/shared/utils/utils'
import type {
  CustomBranding,
  FloorplanListing,
  MaybeEmptyProperty,
  ParkingData,
  State,
} from '@zcode/state-type'
import type { MaybeEmpty } from '@zcode/utility-types'
import { ONE_DAY, isNumber } from '@zcode/utils'
import { compose } from 'redux'
import { getFeedName } from '../../../../../components/detail/helpers/getFeedName'
import { getProviderHref } from '../../../../../components/main/Rail/Listables/STRListItem/helpers/getProviderHref'
import { daysToMonths } from '../../../../../utils/daysToMonths'
import { formatRange } from '../../../../../utils/formatRange'
import { pluralize } from '../../../../../utils/pluralization'
import { selectFilters } from '../../../../filters/selectors/index'
import {
  selectShortTermAvailabilityData,
  selectShortTermAvailabilityFee,
  selectShortTermAvailabilityPrice,
} from '../../../../shortTermAvailability/selectors/index'
import {
  selectVrboAvgNightlyRate,
  selectVrboTaxesAndFees,
} from '../../../../shortTermAvailability/selectors/vrbo'
import { selectDetail } from '../../../selectors/index'
import { getRequiredAndOptionalFees } from '../helpers'

// schema
export const selectEntitySchema = compose((detail) => detail.entity, selectDetail)

// entity
// TODO: Eventually, we'd want to create selectors for every entity property
// We should probably move the sub selectors out into its own file.
export const selectEntity = compose(
  (entitySchema) => (entitySchema ? entitySchema.data : undefined),
  selectEntitySchema
)

export const selectEntityGroupId = compose(
  (entity) => (entity?.buildingId ? -1 * entity.buildingId : entity?.listingId),
  selectEntity
)

export const selectEntityHasAssignedParkingAmmenity = compose((entity) => {
  if (entity?.isBuilding) {
    return !!entity[entity.isActive ? 'activeFloorplans' : 'floorplans']?.some(
      (floorplan: FloorplanListing) =>
        floorplan.amenities?.includes(ListingAmenity.ASSIGNED_PARKING)
    )
  }
  return !!entity?.amenities?.includes(ListingAmenity.ASSIGNED_PARKING)
}, selectEntity)

export const selectEntityHasGarageParkingAmmenity = compose((entity) => {
  if (entity?.isBuilding && !entity.isFloorplan) {
    return !!entity.amenities?.includes(BuildingAmenity.GARAGE_PARKING)
  }
  return false
}, selectEntity)

export const selectEntityHasShow0ForFees = compose((entity) => {
  return !!entity?.features?.includes(PropertyFeatures.SHOW_0_FOR_FEES)
}, selectEntity)

export const selectParkingDataWithFallback = createSelector(
  (state: State): readonly ParkingData[] => {
    const parkingData = selectEntityParkingData(state)
    const hasAssignedParkingAmmenity = selectEntityHasAssignedParkingAmmenity(state)
    const hasGarageParkingAmmenity = selectEntityHasGarageParkingAmmenity(state)
    const resultParkingData: ParkingData[] = []

    const isParkingDataAvailable = !!parkingData?.filter((parking) => parking.type !== '')?.length

    if (isParkingDataAvailable) {
      return parkingData
    }

    if (hasAssignedParkingAmmenity) {
      resultParkingData.push({ type: 'Assigned Parking' })
    }
    if (hasGarageParkingAmmenity) {
      resultParkingData.push({ type: 'Garage Parking' })
    }

    return resultParkingData
  }
)

export const selectEntityHeaderText = compose(
  (entity) => (entity ? entity.headerText : undefined),
  selectEntity
)

const selectEntityAgent = compose((entity) => entity?.agent, selectEntity)

export const selectEntityApplicationFeeInCents = compose(
  (agent) => agent?.applicationFee,
  selectEntityAgent
)

export const selectFees = (state: State) => {
  const entity = selectEntity(state)
  const propertyFees = entity?.fees ?? []
  const applicationFeeInCents = selectEntityApplicationFeeInCents(state)
  const fees = getRequiredAndOptionalFees(propertyFees, applicationFeeInCents)
  return fees
}

export const selectEntityCityName = compose(
  (entity) => (entity ? entity.city : undefined),
  selectEntity
)

export const selectEntityCityState = compose(
  (entity) => (entity ? entity.cityState : undefined),
  selectEntity
)

export const selectEntityCityStateText = compose(
  (entity) => (entity ? entity.cityStateText : undefined),
  selectEntity
)

export const selectEntityState = compose(
  (entity) => (entity ? entity.state : undefined),
  selectEntity
)

export const selectEntityLeaseLengthText = compose((entity) => {
  if (!entity) {
    return null
  }
  const { maxLeaseDays, minLeaseDays } = entity
  const maxLeaseMonths = daysToMonths(maxLeaseDays)
  const minLeaseMonths = daysToMonths(minLeaseDays)
  const maxOrMinLeaseMonths = maxLeaseMonths ?? minLeaseMonths
  if (maxOrMinLeaseMonths == null) {
    return null
  }
  if (minLeaseMonths === 0 && !maxLeaseMonths) {
    return 'Flexible'
  }
  const range = formatRange(minLeaseMonths, maxLeaseMonths)
  return range && (`${range} ${pluralize('month', maxOrMinLeaseMonths)}` as const)
}, selectEntity)

export const selectEntityNeighborhood = compose((entity) => entity?.neighborhood, selectEntity)

export const selectEntityCityStateName = compose(
  (entityHood) => entityHood?.cityStateName,
  selectEntityNeighborhood
)

export const selectEntityParkingData = compose((entity) => {
  if (!entity?.isBuilding || entity.isFloorplan) {
    return null
  }

  return entity?.parkingData
}, selectEntity)

export const selectEntityPets = compose((entity) => entity?.pets, selectEntity)
export const selectEntityPetPolicy = compose((entity) => {
  const petPolicy = entity?.petPolicy
  if (petPolicy && !Array.isArray(petPolicy) && typeof petPolicy._data === 'string') {
    // There's one building in prod that has a string as its petPolicy, rather than
    // an array or object. Ignore that malformed data
    return null
  }
  return petPolicy
}, selectEntity)

export const selectEntityUnitCountRaw = compose((entity) => entity?.units, selectEntity)
export const selectEntityUnitCountCredible = compose(
  (unitCount) => (unitCount && unitCount > 10000 ? null : unitCount),
  selectEntityUnitCountRaw
)

export const selectEntityYearBuiltRaw = compose((entity) => entity?.yearBuilt, selectEntity)
export const selectEntityYearBuiltCredible = createSelector(
  selectNow,
  selectEntityYearBuiltRaw,
  (now, yearBuilt) =>
    yearBuilt && yearBuilt > 1800 && yearBuilt < new Date(now).getFullYear() + 5 ? yearBuilt : null
)

// meta
export const selectEntityMeta = compose((entitySchema) => entitySchema.meta, selectEntitySchema)

export const selectEntityError = compose(selectError, selectEntityMeta)

export const selectEntityFetching = compose(selectFetching, selectEntityMeta)

export const selectEntityFetchingKey = compose(selectFetchingKey, selectEntityMeta)

export const selectEntityIsAvailable = compose((entity) => {
  const { isActive, isBlocked, isMessageable }: MaybeEmptyProperty = entity ?? {}
  return isActive && !isBlocked && isMessageable
}, selectEntity)

/**
 * Returns entity.parsedData or an empty object
 */
export const selectEntityParsedData = compose((entity) => entity?.parsedData ?? {}, selectEntity)

/**
 * Return entity's `custom_branding` or an empty object
 */
export const selectEntityCustomBranding = compose(
  (parsedData): MaybeEmpty<CustomBranding> => parsedData?.custom_branding ?? {},
  selectEntityParsedData
)

export const selectHasUberPromo = compose((parsedData) => {
  return parsedData?.custom_flags === 'uber'
}, selectEntityParsedData)

export const selectListingRentSpecials = compose((parsedData) => {
  return parsedData?.rent_specials
}, selectEntityParsedData)

export const selectIsShortTermViewV1 = compose(
  (entity) => !!entity && entity.isShortTermViewV1,
  selectEntity
)

export const selectBookingProviderName = compose((entity) => {
  const getProviderName = (provider = ''): string => {
    const labels: { [provider: string]: string } = {
      [BOOKINGDOTCOM]: 'Booking.com',
      [EVOLVE]: 'Evolve',
      [JUNE_HOMES]: 'June Homes',
      [RENTALS_UNITED]: 'Rentals United',
      [VRBO]: 'Vrbo',
    }

    return labels[provider] ?? provider
  }

  const feedName = getFeedName(entity)

  return getProviderName(feedName)
}, selectEntity)

export const selectBookingProviderHref = createSelector(
  selectEntity,
  selectFilters,
  (entity, filters) => {
    const { externalUrl } = entity ?? {}
    return getProviderHref({ externalUrl, filters })
  }
)

export const selectAvgNightlyRate = createSelector(
  selectEntity,
  selectVrboAvgNightlyRate,
  selectShortTermAvailabilityPrice,
  selectShortTermAvailabilityData,
  (entity, vrboAvgNightlyPrice, shortTermPrice, shortTermAvailabilityData) => {
    if (entity?.isVRBO) {
      return vrboAvgNightlyPrice
    }
    if (entity?.isRentalsUnited) {
      if (!oisEmpty(shortTermAvailabilityData)) {
        const { startDate, endDate, totalPrice, fee } = shortTermAvailabilityData
        if (startDate && endDate) {
          const numDays = (new Date(endDate).getTime() - new Date(startDate).getTime()) / ONE_DAY
          return String((totalPrice - fee) / numDays)
        }
      }
      return ''
    }
    if (isNumber(shortTermPrice)) {
      return String(shortTermPrice / 100)
    }
    return null
  }
)

export const selectTaxesAndFees = createSelector(
  selectEntity,
  selectVrboTaxesAndFees,
  selectShortTermAvailabilityFee,
  (entity, vrboTaxesAndFees, shortTermFees) => {
    if (entity?.isVRBO) {
      return vrboTaxesAndFees
    }
    if (isNumber(shortTermFees)) {
      return entity?.isRentalsUnited ? String(shortTermFees) : String(shortTermFees / 100)
    }
    return '0'
  }
)

export const selectProviderPricePerNight = createSelector(
  selectEntity,
  selectAvgNightlyRate,
  (entity, avgNightlyRate) => {
    if (entity) {
      const { minPrice } = entity

      return Number.parseFloat(avgNightlyRate?.replace(/[$,]+/g, '') ?? '0') || (minPrice || 0) / 30
    }
    return 0
  }
)
